// Fix WH75 (2026-10-13): pantalla "Pagos del banco". Tesoreria sube el archivo de
// transferencias de BBVA Net Cash (CSV o Excel) y el sistema lo concilia contra
// Facturas y Pagos. Reemplaza a la pantalla Bancos (Syncfy / host to host).
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, RefreshCw, Download, CheckCircle2, AlertTriangle, Search, X, Loader2, Landmark,
  Link2, EyeOff, Undo2, Trash2, ChevronDown, ChevronRight, FileSpreadsheet,
} from "lucide-react";

const ESTADO = {
  conciliado: ["Conciliado", "bg-green-100 text-green-800 border-green-200"],
  aplicado: ["Pago registrado", "bg-green-100 text-green-800 border-green-200"],
  por_aplicar: ["Por registrar", "bg-blue-100 text-blue-800 border-blue-200"],
  con_diferencia: ["Con diferencia", "bg-amber-100 text-amber-800 border-amber-200"],
  requiere_revision: ["Revisar", "bg-amber-100 text-amber-800 border-amber-200"],
  ambiguo: ["Elegir factura", "bg-amber-100 text-amber-800 border-amber-200"],
  sin_factura: ["Sin factura", "bg-red-100 text-red-800 border-red-200"],
  posible_doble_pago: ["Posible doble pago", "bg-red-100 text-red-800 border-red-200"],
  ignorado: ["Ignorado", "bg-slate-100 text-slate-600 border-slate-200"],
  no_aplica: ["No exitosa en el banco", "bg-slate-100 text-slate-600 border-slate-200"],
  pendiente: ["Pendiente", "bg-slate-100 text-slate-600 border-slate-200"],
};

const GRUPOS = [
  { key: "todos", label: "Todas", estados: [], tono: "border-slate-300" },
  { key: "por_aplicar", label: "Por registrar", estados: ["por_aplicar"], tono: "border-blue-300" },
  { key: "revisar", label: "Por revisar", estados: ["con_diferencia", "requiere_revision", "ambiguo", "posible_doble_pago"], tono: "border-amber-300" },
  { key: "sin_factura", label: "Sin factura", estados: ["sin_factura"], tono: "border-red-300" },
  { key: "conciliadas", label: "Conciliadas", estados: ["conciliado", "aplicado"], tono: "border-green-300" },
  { key: "otras", label: "Ignoradas / no exitosas", estados: ["ignorado", "no_aplica"], tono: "border-slate-300" },
];

const CATEGORIAS = [
  ["nomina", "Nomina"], ["impuestos", "Impuestos"], ["servicios", "Servicios (luz, renta, etc.)"],
  ["traspaso", "Traspaso entre cuentas"], ["anticipo", "Anticipo a proveedor"],
  ["gasto_sin_factura", "Gasto sin factura"], ["otro", "Otro"],
];

const fmt = (n) => (n === null || n === undefined ? "" : Number(n).toLocaleString("es-MX", { style: "currency", currency: "MXN" }));

function mensajeError(e, porDefecto) {
  const d = e?.response?.data?.detail;
  if (!d) return porDefecto;
  if (typeof d === "string") return d;
  if (d.mensaje) return `${d.mensaje} ${(d.bloqueantes || []).join(" ")}`;
  return porDefecto;
}

function Badge({ estado }) {
  const [txt, cls] = ESTADO[estado] || [estado, "bg-slate-100 text-slate-600 border-slate-200"];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${cls}`}>{txt}</span>;
}

export default function OpsPagosBanco() {
  const { user } = useAuth();
  const puedePagar = !!user?.can_create_payments;
  const esAdmin = user?.role === "corporate_admin";
  const fileRef = useRef(null);
  const [resumen, setResumen] = useState(null);
  const [filas, setFilas] = useState({ total: 0, items: [] });
  const [archivos, setArchivos] = useState([]);
  const [erp, setErp] = useState(null);
  const [grupo, setGrupo] = useState("todos");
  const [q, setQ] = useState("");
  const [archivoId, setArchivoId] = useState("");
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [ultimaCarga, setUltimaCarga] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [confirmarLote, setConfirmarLote] = useState(false);
  const [verErp, setVerErp] = useState(false);
  const [verCargas, setVerCargas] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  const estadosDelGrupo = GRUPOS.find((g) => g.key === grupo)?.estados || [];

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = {};
      if (archivoId) params.file_id = archivoId;
      const [r, f, a] = await Promise.all([
        api.get("/ops/bank-payments/summary", { params }),
        api.get("/ops/bank-payments/rows", { params: { ...params, state: estadosDelGrupo.join(",") || undefined, q: q || undefined, limit: 300 } }),
        api.get("/ops/bank-payments/files"),
      ]);
      setResumen(r.data);
      setFilas(f.data);
      setArchivos((a.data || []).filter((x) => x.status === "activo"));
    } catch (e) {
      toast.error(mensajeError(e, "No se pudo cargar la conciliacion."));
    } finally {
      setCargando(false);
    }
  }, [archivoId, grupo, q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(cargar, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, q]);

  const cargarErp = useCallback(async () => {
    try {
      const r = await api.get("/ops/bank-payments/erp-sin-banco");
      setErp(r.data);
    } catch (e) {
      toast.error(mensajeError(e, "No se pudo consultar los pagos del sistema."));
    }
  }, []);

  useEffect(() => { if (verErp) cargarErp(); }, [verErp, cargarErp, resumen?.total]);

  const subir = async (archivo) => {
    if (!archivo) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", archivo);
      const r = await api.post("/ops/bank-payments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUltimaCarga(r.data);
      setGrupo("todos");
      setArchivoId("");
      await cargar();
      if (r.data.ya_estaba_cargado) toast.info("Ese archivo ya estaba cargado. No se duplico nada.");
      else toast.success(`${r.data.nuevas} transferencias nuevas, ${r.data.repetidas} ya estaban.`);
    } catch (e) {
      toast.error(mensajeError(e, "No se pudo leer el archivo."));
    } finally {
      setSubiendo(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const aplicarLote = async () => {
    setOcupado(true);
    try {
      const r = await api.post("/ops/bank-payments/apply-bulk", { file_id: archivoId || undefined });
      toast.success(`${r.data.aplicadas} pagos registrados.`);
      if (r.data.fallidas?.length) toast.error(`${r.data.fallidas.length} no se pudieron registrar; revisalos uno por uno.`);
      setConfirmarLote(false);
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, "No se pudieron registrar los pagos."));
    } finally {
      setOcupado(false);
    }
  };

  const reconciliar = async () => {
    setOcupado(true);
    try {
      await api.post("/ops/bank-payments/rematch");
      toast.success("Listo: se volvio a cruzar lo pendiente.");
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, "No se pudo volver a conciliar."));
    } finally {
      setOcupado(false);
    }
  };

  const exportar = async () => {
    try {
      const r = await api.get("/ops/bank-payments/export.xlsx", { params: { file_id: archivoId || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "conciliacion_pagos_banco.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo exportar.");
    }
  };

  const anularCarga = async (f) => {
    if (!window.confirm(`Anular la carga "${f.filename}"? Se borran sus transferencias para poder subir un archivo corregido.`)) return;
    try {
      await api.delete(`/ops/bank-payments/files/${f.id}`);
      toast.success("Carga anulada.");
      if (archivoId === f.id) setArchivoId("");
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, "No se pudo anular la carga."));
    }
  };

  const abrir = async (fila) => {
    try {
      const r = await api.get(`/ops/bank-payments/rows/${fila.id}`);
      setDetalle(r.data);
    } catch (e) {
      toast.error(mensajeError(e, "No se pudo abrir la transferencia."));
    }
  };

  const aplicarUna = async (fila) => {
    setOcupado(true);
    try {
      await api.post(`/ops/bank-payments/rows/${fila.id}/apply`, {});
      toast.success("Pago registrado.");
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, "No se pudo registrar el pago."));
    } finally {
      setOcupado(false);
    }
  };

  const listos = resumen?.por_estado?.por_aplicar?.n || 0;
  const cardsDe = (g) => {
    const est = g.estados.length ? g.estados : Object.keys(resumen?.por_estado || {});
    return est.reduce((a, k) => ({ n: a.n + (resumen?.por_estado?.[k]?.n || 0), monto: a.monto + (resumen?.por_estado?.[k]?.monto || 0) }), { n: 0, monto: 0 });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="ops-pagos-banco-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Landmark className="w-6 h-6 text-yellow-500" /> Pagos del banco
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Sube el archivo de transferencias de BBVA Net Cash y se concilia con Facturas y Pagos. Si subes el mismo archivo dos veces no se duplica nada.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={reconciliar} disabled={ocupado} data-testid="btn-reconciliar"
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${ocupado ? "animate-spin" : ""}`} /> Volver a conciliar
          </button>
          <button onClick={exportar} data-testid="btn-exportar"
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Download className="w-4 h-4" /> Exportar Excel
          </button>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={(e) => { e.preventDefault(); setArrastrando(false); subir(e.dataTransfer.files?.[0]); }}
        onClick={() => !subiendo && fileRef.current?.click()}
        data-testid="zona-subir"
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${arrastrando ? "border-yellow-400 bg-yellow-50" : "border-slate-300 hover:border-slate-400 bg-white"}`}
      >
        <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx,.xlsm" className="hidden" data-testid="input-archivo"
          onChange={(e) => subir(e.target.files?.[0])} />
        {subiendo ? (
          <div className="flex items-center justify-center gap-2 text-slate-600"><Loader2 className="w-5 h-5 animate-spin" /> Leyendo y conciliando el archivo...</div>
        ) : (
          <div className="space-y-1">
            <Upload className="w-7 h-7 mx-auto text-slate-400" />
            <div className="text-sm font-semibold text-slate-800">Arrastra aqui el archivo del banco o haz clic para elegirlo</div>
            <div className="text-xs text-slate-500">CSV o Excel (.xlsx) de transferencias realizadas de BBVA Net Cash. Se lee, se guarda y se concilia en un solo paso.</div>
          </div>
        )}
      </div>

      {ultimaCarga && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm space-y-1" data-testid="resultado-carga">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-900 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-slate-500" /> {ultimaCarga.file?.filename}
            </div>
            <button onClick={() => setUltimaCarga(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
          </div>
          <div className="text-slate-600">
            {ultimaCarga.ya_estaba_cargado
              ? "Este archivo ya estaba cargado: no se duplico nada."
              : `${ultimaCarga.file?.rows_total} transferencias leidas del ${ultimaCarga.file?.date_from} al ${ultimaCarga.file?.date_to} por ${fmt(ultimaCarga.file?.total_amount)}: ${ultimaCarga.nuevas} nuevas y ${ultimaCarga.repetidas} que ya estaban.`}
          </div>
          {(ultimaCarga.advertencias || []).length > 0 && (
            <div className="text-amber-700 text-xs flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> {ultimaCarga.advertencias.slice(0, 3).join(" ")}{ultimaCarga.advertencias.length > 3 ? ` (+${ultimaCarga.advertencias.length - 3} mas)` : ""}</div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {GRUPOS.map((g) => {
          const c = cardsDe(g);
          return (
            <button key={g.key} onClick={() => setGrupo(g.key)} data-testid={`card-${g.key}`}
              className={`text-left rounded-xl border-2 p-3 bg-white transition-all ${grupo === g.key ? `${g.tono} shadow-sm` : "border-transparent hover:border-slate-200"}`}>
              <div className="text-xs font-semibold text-slate-500">{g.label}</div>
              <div className="text-2xl font-bold text-slate-900">{c.n}</div>
              <div className="text-xs text-slate-500">{fmt(c.monto)}</div>
            </button>
          );
        })}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar proveedor, concepto o folio" data-testid="buscar"
            className="pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-72 bg-white" />
        </div>
        <select value={archivoId} onChange={(e) => setArchivoId(e.target.value)} data-testid="filtro-archivo"
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">Todas las cargas</option>
          {archivos.map((f) => <option key={f.id} value={f.id}>{f.filename} ({f.date_from} al {f.date_to})</option>)}
        </select>
        {puedePagar && listos > 0 && (
          <button onClick={() => setConfirmarLote(true)} data-testid="btn-aplicar-lote"
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400">
            <CheckCircle2 className="w-4 h-4" /> Registrar los pagos listos
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="tabla-filas">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
              <th className="p-3">Fecha</th><th className="p-3">Destinatario</th><th className="p-3">Concepto</th>
              <th className="p-3 text-right">Importe</th><th className="p-3">Estado</th><th className="p-3">Factura / proveedor</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {cargando && filas.items.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /> Cargando...</td></tr>
            )}
            {!cargando && filas.items.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400" data-testid="sin-filas">
                {resumen?.total ? "No hay transferencias en este filtro." : "Todavia no hay transferencias. Sube el archivo del banco para empezar."}
              </td></tr>
            )}
            {filas.items.map((f) => (
              <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`fila-${f.id}`}>
                <td className="p-3 whitespace-nowrap text-slate-700">{f.fecha}</td>
                <td className="p-3"><div className="font-medium text-slate-800">{f.destinatario}</div><div className="text-xs text-slate-400">{f.tipo}{f.referencia ? ` · ref ${f.referencia}` : ""}</div></td>
                <td className="p-3 text-slate-700">{f.concepto}</td>
                <td className="p-3 text-right font-semibold text-slate-900 whitespace-nowrap">{fmt(f.importe)}</td>
                <td className="p-3"><Badge estado={f.match_state} /></td>
                <td className="p-3 text-slate-600">
                  <div>{(f.match?.folios || []).join(", ") || "-"}</div>
                  <div className="text-xs text-slate-400">{f.supplier_name || ""}</div>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {f.match_state === "por_aplicar" && puedePagar && (
                    <button onClick={() => aplicarUna(f)} disabled={ocupado} data-testid={`aplicar-${f.id}`}
                      className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 mr-2">Registrar pago</button>
                  )}
                  <button onClick={() => abrir(f)} data-testid={`ver-${f.id}`}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-white">
                    {["conciliado", "aplicado", "no_aplica"].includes(f.match_state) ? "Ver" : "Revisar"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filas.total > filas.items.length && (
          <div className="p-3 text-xs text-slate-500 text-center">Mostrando {filas.items.length} de {filas.total}. Usa el buscador o los filtros para acotar.</div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <button onClick={() => setVerErp(!verErp)} data-testid="toggle-erp" className="w-full flex items-center gap-2 p-4 text-left text-sm font-semibold text-slate-800">
          {verErp ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          En el sistema pero no en el banco
        </button>
        {verErp && (
          <div className="px-4 pb-4 text-sm" data-testid="panel-erp">
            <p className="text-xs text-slate-500 mb-2">
              Pagos registrados en Pagos, con fecha dentro del periodo que cubren los archivos cargados, que no aparecen en ninguno.
              Puede ser un pago que salio por otra cuenta o un archivo que falta subir.
            </p>
            {!erp ? <Loader2 className="w-4 h-4 animate-spin" /> : erp.items.length === 0 ? (
              <div className="text-slate-500">Nada pendiente de aparecer en el banco{erp.desde ? ` entre ${erp.desde} y ${erp.hasta}` : ""}.</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Fecha</th><th>Proveedor</th><th>Factura</th><th className="text-right">Monto</th><th>Referencia</th></tr></thead>
                <tbody>{erp.items.map((p) => (
                  <tr key={p.payment_id} className="border-t border-slate-100"><td className="py-1">{p.fecha}</td><td>{p.proveedor}</td><td>{p.folio}</td><td className="text-right">{fmt(p.monto)}</td><td>{p.referencia}</td></tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <button onClick={() => setVerCargas(!verCargas)} data-testid="toggle-cargas" className="w-full flex items-center gap-2 p-4 text-left text-sm font-semibold text-slate-800">
          {verCargas ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Cargas anteriores ({archivos.length})
        </button>
        {verCargas && (
          <div className="px-4 pb-4 text-sm space-y-2" data-testid="panel-cargas">
            {archivos.length === 0 && <div className="text-slate-500">Todavia no hay cargas.</div>}
            {archivos.map((f) => (
              <div key={f.id} className="flex items-center justify-between border-t border-slate-100 pt-2">
                <div>
                  <div className="font-medium text-slate-800">{f.filename}</div>
                  <div className="text-xs text-slate-500">{f.rows_total} transferencias · {f.date_from} al {f.date_to} · {fmt(f.total_amount)} · subido por {f.uploaded_by_name || "-"} el {String(f.uploaded_at).slice(0, 10)}</div>
                </div>
                <button onClick={() => anularCarga(f)} className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600" title="Anular carga"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmarLote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && setConfirmarLote(false)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md space-y-4" data-testid="modal-lote">
            <h3 className="font-bold text-slate-900">Registrar los pagos listos</h3>
            <p className="text-sm text-slate-600">
              Se registran como pagadas las facturas de las transferencias marcadas "Por registrar" con folio y monto exactos y proveedor identificado, sin advertencias.
              Las facturas pasan a pagadas y al proveedor le llega el aviso. Las que tengan cualquier duda no se tocan.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmarLote(false)} className="flex-1 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={aplicarLote} disabled={ocupado} data-testid="confirmar-lote"
                className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
                {ocupado ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Registrar
              </button>
            </div>
          </div>
        </div>
      )}

      {detalle && (
        <Detalle fila={detalle} puedePagar={puedePagar} esAdmin={esAdmin}
          onCerrar={() => setDetalle(null)} onCambio={async () => { setDetalle(null); await cargar(); }} />
      )}
    </div>
  );
}

function Detalle({ fila, puedePagar, esAdmin, onCerrar, onCambio }) {
  const [seleccion, setSeleccion] = useState((fila.match?.invoice_ids || []).slice(0, 1));
  const [busqueda, setBusqueda] = useState("");
  const [encontradas, setEncontradas] = useState([]);
  const [bloqueantes, setBloqueantes] = useState([]);
  const [motivo, setMotivo] = useState("");
  const [categoria, setCategoria] = useState("otro");
  const [motivoIgnorar, setMotivoIgnorar] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const cerrada = ["aplicado", "conciliado", "no_aplica"].includes(fila.match_state);
  const m = fila.match || {};

  useEffect(() => {
    if (!busqueda.trim()) { setEncontradas([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/ops/bank-payments/invoices-search", { params: { q: busqueda, supplier_id: fila.supplier_id || undefined } });
        setEncontradas(r.data || []);
      } catch (e) { setEncontradas([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda, fila.supplier_id]);

  const candidatas = [...(m.candidatos || []).map((c) => ({ id: c.invoice_id, folio: c.folio, proveedor: c.supplier_name, total: c.total, status: c.status })),
    ...encontradas.map((c) => ({ id: c.id, folio: c.folio_fiscal, proveedor: c.supplier_name, total: c.total, status: c.status }))]
    .filter((c, i, a) => a.findIndex((x) => x.id === c.id) === i);
  (fila.facturas || []).forEach((c) => {
    if (!candidatas.find((x) => x.id === c.id)) candidatas.push({ id: c.id, folio: c.folio_fiscal, proveedor: m.proveedor || fila.supplier_name, total: c.total, status: c.status });
  });

  const alternar = (id) => setSeleccion((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const ejecutar = async (fn, ok) => {
    setOcupado(true);
    try { await fn(); toast.success(ok); await onCambio(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.bloqueantes) { setBloqueantes(d.bloqueantes); toast.error("Hay advertencias; leelas abajo."); }
      else toast.error(mensajeError(e, "No se pudo completar la accion."));
    } finally { setOcupado(false); }
  };

  const registrar = (forzar) => ejecutar(
    () => api.post(`/ops/bank-payments/rows/${fila.id}/apply`, { invoice_ids: seleccion.length ? seleccion : undefined, forzar, motivo: forzar ? motivo : undefined }),
    "Pago registrado.");

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4" data-testid="modal-detalle">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-bold text-slate-900 text-lg">{fila.destinatario}</h3>
            <div className="text-sm text-slate-500">{fila.fecha} · {fila.tipo}{fila.referencia ? ` · ref ${fila.referencia}` : ""} · {fila.concepto}</div>
          </div>
          <div className="text-right"><div className="text-xl font-bold text-slate-900">{fmt(fila.importe)}</div><Badge estado={fila.match_state} /></div>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
          <div className="text-xs font-semibold text-slate-500 mb-1">Por que quedo asi</div>
          {(m.motivos || []).map((t, i) => <div key={i} className="text-slate-700">{t}</div>)}
          {(m.avisos_bloqueantes || []).map((t, i) => <div key={`b${i}`} className="text-amber-700 flex gap-1"><AlertTriangle className="w-4 h-4 shrink-0" />{t}</div>)}
          {(m.avisos || []).map((t, i) => <div key={`a${i}`} className="text-slate-500">{t}</div>)}
          {fila.supplier_name && <div className="text-slate-500">Proveedor identificado: <b>{fila.supplier_name}</b>{fila.supplier_via ? ` (por ${fila.supplier_via.replace("+", " y ")})` : ""}</div>}
          {m.diferencia ? <div className="text-slate-700">Diferencia contra la factura: <b>{fmt(m.diferencia)}</b></div> : null}
        </div>

        {!cerrada && fila.match_state !== "ignorado" && (
          <div className="space-y-2" data-testid="seccion-elegir">
            <div className="text-xs font-semibold text-slate-500">Factura que paga esta transferencia</div>
            {candidatas.length === 0 && <div className="text-sm text-slate-500">No hay candidatas. Busca la factura por folio o proveedor.</div>}
            {candidatas.map((c) => (
              <label key={c.id} className="flex items-center gap-2 border border-slate-200 rounded-lg p-2 text-sm cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={seleccion.includes(c.id)} onChange={() => alternar(c.id)} />
                <span className="font-medium">{c.folio}</span><span className="text-slate-500">{c.proveedor}</span>
                <span className="ml-auto font-semibold">{fmt(c.total)}</span><span className="text-xs text-slate-400">{c.status}</span>
              </label>
            ))}
            <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar otra factura por folio o proveedor" data-testid="buscar-factura"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          </div>
        )}

        {bloqueantes.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm space-y-2" data-testid="bloqueantes">
            <div className="font-semibold text-amber-900">Antes de registrar el pago:</div>
            {bloqueantes.map((b, i) => <div key={i} className="text-amber-800">{b}</div>)}
            {esAdmin && puedePagar ? (
              <div className="space-y-2">
                <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo para registrarlo de todos modos (minimo 10 caracteres)" data-testid="motivo-forzar"
                  className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white" rows={2} />
                <button onClick={() => registrar(true)} disabled={ocupado || motivo.trim().length < 10} data-testid="btn-forzar"
                  className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold disabled:opacity-50">Registrar de todos modos</button>
              </div>
            ) : <div className="text-xs text-amber-800">Solo un administrador corporativo puede registrarlo con estas advertencias.</div>}
          </div>
        )}

        {fila.match_state === "ignorado" && (
          <div className="text-sm text-slate-600">Ignorada como <b>{fila.ignored_category}</b>{fila.ignored_reason ? `: ${fila.ignored_reason}` : ""}.</div>
        )}

        {!cerrada && fila.match_state !== "ignorado" && (
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="text-xs font-semibold text-slate-500">No es un pago a proveedor con factura</div>
            <div className="flex gap-2 flex-wrap">
              <select value={categoria} onChange={(e) => setCategoria(e.target.value)} data-testid="categoria-ignorar" className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                {CATEGORIAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input value={motivoIgnorar} onChange={(e) => setMotivoIgnorar(e.target.value)} placeholder="Nota (opcional)" className="flex-1 min-w-[10rem] border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
              <button onClick={() => ejecutar(() => api.post(`/ops/bank-payments/rows/${fila.id}/ignore`, { categoria, motivo: motivoIgnorar }), "Marcada como ignorada.")} disabled={ocupado} data-testid="btn-ignorar"
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"><EyeOff className="w-4 h-4" /> Ignorar</button>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end flex-wrap pt-1">
          {fila.match_state === "ignorado" && (
            <button onClick={() => ejecutar(() => api.post(`/ops/bank-payments/rows/${fila.id}/reopen`), "Reabierta.")} disabled={ocupado}
              className="flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-sm"><Undo2 className="w-4 h-4" /> Reabrir</button>
          )}
          {!cerrada && fila.match_state !== "ignorado" && (
            <>
              <button onClick={() => ejecutar(() => api.post(`/ops/bank-payments/rows/${fila.id}/link`, { invoice_ids: seleccion }), "Factura vinculada.")}
                disabled={ocupado || seleccion.length === 0} data-testid="btn-vincular"
                className="flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"><Link2 className="w-4 h-4" /> Solo vincular</button>
              {puedePagar && (
                <button onClick={() => registrar(false)} disabled={ocupado || (seleccion.length === 0 && !(m.invoice_ids || []).length)} data-testid="btn-registrar"
                  className="flex items-center gap-1 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
                  {ocupado ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Registrar pago
                </button>
              )}
            </>
          )}
          <button onClick={onCerrar} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">Cerrar</button>
        </div>
      </div>
    </div>
  );
}
